/**
 * The Email control on an answer, a dashboard or a document.
 *
 * Downloads an editable Outlook draft. Recipients, subject and Send stay with
 * the user in Outlook.
 */
import { Mail } from "lucide-react";
import { useEffect, useState } from "react";
import { emailDraftConfig } from "./config";
import {
  useEmailDraft,
  type EmailDraftSource,
  type EmailSourceKind,
} from "./useEmailDraft";

interface EmailButtonProps {
  traceId: string;
  kind: EmailSourceKind;
  prepare: () => EmailDraftSource | Promise<EmailDraftSource>;
  disabled?: boolean;
  disabledReason?: string;
  /** Hides the label, for the row under a chat reply. */
  compact?: boolean;
}

export function EmailButton({
  traceId,
  kind,
  prepare,
  disabled = false,
  disabledReason,
  compact = false,
}: EmailButtonProps) {
  const [open, setOpen] = useState(false);
  const { busy, notice, download, dismiss } = useEmailDraft({
    traceId,
    kind,
    prepare,
  });

  useEffect(() => {
    if (!open) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  if (!emailDraftConfig().enabled) return null;

  return (
    <div className={`email-draft ${compact ? "compact" : ""}`}>
      <button
        type="button"
        className="button tertiary email-draft-trigger"
        onClick={() => setOpen((value) => !value)}
        disabled={disabled || busy}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={compact ? "Email this" : undefined}
        title={disabled ? disabledReason : "Prepare an Outlook email with this content"}
      >
        <Mail size={15} className={busy ? "spinning" : ""} />
        {!compact && (busy ? "Preparing…" : "Email")}
      </button>

      {open && (
        <div className="export-audience email-draft-menu" role="menu" aria-label="Email">
          <button
            type="button"
            role="menuitem"
            autoFocus
            onClick={() => {
              setOpen(false);
              void download();
            }}
          >
            <span className="export-audience-label">Download Outlook draft</span>
            <span className="export-audience-detail">
              {kind === "answer"
                ? "An editable .eml draft with the answer in the body."
                : "An editable .eml draft with charts and the report attached."}
            </span>
          </button>
          <button
            type="button"
            className="export-audience-cancel"
            onClick={() => setOpen(false)}
          >
            Cancel
          </button>
        </div>
      )}

      {notice && (
        <div
          className={`inline-notice ${notice.tone} email-draft-notice`}
          role={notice.tone === "warning" ? "alert" : "status"}
        >
          <span>{notice.text}</span>
          <button type="button" className="button tertiary" onClick={dismiss}>
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
}

export default EmailButton;
